import { Link } from 'react-router-dom';
import { Shield, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { useInView } from '@/hooks/useInView';

const developerTrustPoints = [
  'Team-level insights only — no individual ranking',
  'No keystroke tracking, no screen time monitoring',
  'Engineers request it — surfaces blockers frustrating them too',
  'Developers see the same dashboard as managers',
];

// The heading and the paragraph already carry the headline claim, so these
// bullets add distinct evidence rather than restating it - including the honest
// disclosure, which reads as confidence next to the claim, not as a caveat.
const dataSecurityPoints = [
  'Read-only GitHub App, zero write permissions',
  'The only code we store is the snippet on an inline review comment',
  'SOC 2-aligned controls',
  'Revoke anytime',
];

export default function SecurityFirst() {
  const [sectionRef, isInView] = useInView<HTMLElement>({ threshold: 0.1 });

  return (
    <section ref={sectionRef} className="py-8 bg-gray-50 dark:bg-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Developer Trust Card */}
          <div
            className={`bg-white dark:bg-gray-700 rounded-2xl border border-gray-200 dark:border-gray-600 border-l-4 border-l-primary-500 p-6 sm:p-8 shadow-sm ${
              isInView ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full">
                <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Built for Teams, Not Surveillance
              </h2>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-5">
              We built CodePulse because the best engineering leaders don&apos;t need to watch
              people work — they need to understand how work flows through the system. Every
              metric is team-level by default.
            </p>

            <div className="space-y-2.5">
              {developerTrustPoints.map((point) => (
                <div key={point} className="flex items-start gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-primary-500 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">{point}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Data Security Card */}
          <div
            className={`bg-white dark:bg-gray-700 rounded-2xl border border-gray-200 dark:border-gray-600 p-6 sm:p-8 shadow-sm ${
              isInView ? 'animate-fade-in-up animation-delay-150' : 'opacity-0'
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full">
                <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Your Code Stays Yours. Always.
              </h2>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-5">
              We analyze pull requests, commits, reviews, and delivery signals. Your code stays
              yours - we never clone your repository or read your files.
            </p>

            <div className="space-y-2.5 mb-5">
              {dataSecurityPoints.map((point) => (
                <div key={point} className="flex items-start gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">{point}</span>
                </div>
              ))}
            </div>

            <Link
              to="/security"
              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium transition-colors text-sm"
            >
              Read our full security documentation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
